import { History, MessageSquare, Lock, Unlock, User } from 'lucide-react';
import { isLocked, labelFor, normalizeStatus } from '../utils/configItemLifecycle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';

interface StatusHistoryEntry {
  fromStatus: string;
  toStatus: string;
  changedBy: {
    userId: string;
    userName: string;
    userEmail: string;
    role: 'user' | 'companyAdmin' | 'consultant';
  };
  changedAt: string; // Human readable
  timestamp: string; // ISO timestamp
  comment?: string;
  automated?: boolean;
}

interface CIStatusHistoryProps {
  statusHistory?: StatusHistoryEntry[];
  unlockHistory?: Array<{
    unlockedBy: {
      userId: string;
      userName: string;
      userEmail: string;
    };
    unlockedAt: string;
    reason: string;
    reLockedAt?: string;
  }>;
}

export function CIStatusHistory({ statusHistory = [], unlockHistory = [] }: CIStatusHistoryProps) {
  // The one lifecycle status (docs/CONFIG_ITEM_LIFECYCLE.md); old entries' spellings fold into it.
  const getStatusLabel = (status: string) => (status === 'cancelled' ? 'Canceled' : labelFor(status));

  // Get badge color for status
  const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    const lifecycle = normalizeStatus(status);
    if (lifecycle === 'approved') return 'default';
    if (isLocked(lifecycle)) return 'secondary';
    if (status === 'cancelled' || lifecycle === 'blocked') return 'destructive';
    return 'outline';
  };

  // Get role badge color
  const getRoleBadge = (role: string) => {
    const colors: Record<string, string> = {
      'consultant': 'bg-purple-100 text-purple-800',
      'companyAdmin': 'bg-blue-100 text-blue-800',
      'user': 'bg-slate-100 text-slate-800',
    };
    const labels: Record<string, string> = {
      'consultant': 'Consultant',
      'companyAdmin': 'Admin',
      'user': 'User',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${colors[role] || colors.user}`}>
        {labels[role] || 'User'}
      </span>
    );
  };

  // Combine and sort all history events
  const allEvents = [
    ...statusHistory.map(entry => ({
      type: 'status',
      timestamp: entry.timestamp,
      displayTime: entry.changedAt,
      data: entry,
    })),
    ...unlockHistory.map(entry => ({
      type: 'unlock',
      timestamp: entry.unlockedAt,
      displayTime: new Date(entry.unlockedAt).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }),
      data: entry,
    })),
  ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  if (allEvents.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="size-5" />
            Status History
          </CardTitle>
          <CardDescription>No status changes yet</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <History className="size-5" />
          Status History
        </CardTitle>
        <CardDescription>
          Complete audit trail of all status changes and unlock events
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            {allEvents.map((event, index) => (
              <div key={index} className="relative pl-6 pb-4 border-l-2 border-slate-200 last:border-l-0 last:pb-0">
                {/* Timeline dot */}
                <div className="absolute left-[-9px] top-0 size-4 rounded-full bg-white border-2 border-slate-400" />

                {event.type === 'status' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <User className="size-3.5" />
                      <span>
                        {(event.data as StatusHistoryEntry).changedBy.userName}
                      </span>
                      {getRoleBadge((event.data as StatusHistoryEntry).changedBy.role)}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-slate-600">changed status:</span>
                      <Badge variant={getStatusBadgeVariant((event.data as StatusHistoryEntry).fromStatus)}>
                        {getStatusLabel((event.data as StatusHistoryEntry).fromStatus)}
                      </Badge>
                      <span className="text-slate-400">→</span>
                      <Badge variant={getStatusBadgeVariant((event.data as StatusHistoryEntry).toStatus)}>
                        {getStatusLabel((event.data as StatusHistoryEntry).toStatus)}
                      </Badge>
                    </div>

                    {(event.data as StatusHistoryEntry).comment && (
                      <div className="flex gap-2 mt-2">
                        <MessageSquare className="size-4 text-slate-400 shrink-0 mt-0.5" />
                        <p className="text-sm text-slate-700 italic bg-slate-50 p-2 rounded">
                          "{(event.data as StatusHistoryEntry).comment}"
                        </p>
                      </div>
                    )}

                    <p className="text-xs text-slate-500 mt-1">
                      {event.displayTime}
                    </p>
                  </div>
                )}

                {event.type === 'unlock' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-orange-600">
                      <Unlock className="size-3.5" />
                      <span>
                        {(event.data as any).unlockedBy.userName}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-800">
                        Consultant
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Lock className="size-4 text-orange-600" />
                      <span className="text-sm text-slate-700">
                        Unlocked requirements for editing
                      </span>
                    </div>

                    {(event.data as any).reason && (
                      <div className="flex gap-2 mt-2">
                        <MessageSquare className="size-4 text-slate-400 shrink-0 mt-0.5" />
                        <p className="text-sm text-slate-700 italic bg-orange-50 p-2 rounded">
                          "{(event.data as any).reason}"
                        </p>
                      </div>
                    )}

                    <p className="text-xs text-slate-500 mt-1">
                      {event.displayTime}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
